import { Award, Eye, HeartHandshake, Lightbulb, ShieldCheck, Sparkles, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CATEGORY_LABEL, scrapRate, type Achievement, type AchievementCategory } from '@/features/workMode/gamification';
import { useGamification } from '@/features/workMode/useGamification';
import type { WorkProfileStats } from '@/features/workMode/types';
import { formatDuration } from '@/features/workMode/api';

const CATEGORY_ICON: Record<AchievementCategory, typeof Award> = {
  quality: Eye,
  safety: ShieldCheck,
  voice: Lightbulb,
  output: Zap,
  wellbeing: HeartHandshake,
};

const TIER_RING: Record<Achievement['tier'], string> = {
  bronze: 'border-fresh-salmon/70 bg-fresh-salmon/15 text-fresh-salmon-dark',
  silver: 'border-atmosphere/80 bg-atmosphere/25 text-atmosphere-dark',
  gold: 'border-bright-moss bg-bright-moss/35 text-bright-moss-dark',
};

/** Level ring + title + progress to the next level. */
export function LevelBadge({ stats, size = 'md', className }: { stats: WorkProfileStats; size?: 'md' | 'lg'; className?: string }) {
  const g = useGamification(stats);
  if (!g) return null;
  const r = size === 'lg' ? 44 : 30;
  const stroke = size === 'lg' ? 6 : 4;
  const c = 2 * Math.PI * r;
  const dash = c * g.level.progress;
  const dim = (r + stroke) * 2;
  return (
    <div className={cn('flex items-center gap-4', className)}>
      <div className="relative shrink-0" style={{ width: dim, height: dim }}>
        <svg width={dim} height={dim} className="-rotate-90">
          <circle cx={dim / 2} cy={dim / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth={stroke} />
          <circle
            cx={dim / 2}
            cy={dim / 2}
            r={r}
            fill="none"
            stroke="#335C2C"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c - dash}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn('font-medium leading-none text-foreground', size === 'lg' ? 'text-3xl' : 'text-xl')}>{g.level.level}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">level</span>
        </div>
      </div>
      <div className="min-w-0">
        <p className={cn('font-medium text-foreground', size === 'lg' ? 'text-xl' : 'text-base')}>{g.level.title}</p>
        <p className="text-sm text-muted-foreground">
          {g.points.toLocaleString()} pts
          {g.level.next ? ` · ${(g.level.next - g.points).toLocaleString()} to next level` : ' · top level'}
        </p>
        {g.multiplier !== 1 && (
          <p className={cn('mt-0.5 text-xs font-medium', g.multiplier > 1 ? 'text-bright-moss-dark' : 'text-fresh-salmon-dark')}>
            <Sparkles className="mr-1 inline h-3 w-3" />
            Quality multiplier ×{g.multiplier.toFixed(2)}
            {g.scrapRate !== null && ` (${(g.scrapRate * 100).toFixed(1)}% scrap)`}
          </p>
        )}
      </div>
    </div>
  );
}

/** Badge grid, earned first. `compact` hides the unearned ones. */
export function AchievementGrid({
  stats,
  compact = false,
  className,
}: {
  stats: WorkProfileStats;
  compact?: boolean;
  className?: string;
}) {
  const g = useGamification(stats);
  if (!g) return null;
  const list = [...g.achievements].sort((a, b) => Number(b.earned) - Number(a.earned) || b.progress - a.progress);
  const shown = compact ? list.filter((a) => a.earned) : list;
  if (shown.length === 0) {
    return <p className={cn('text-sm text-muted-foreground', className)}>No badges yet.</p>;
  }
  return (
    <ul className={cn('grid gap-2 sm:grid-cols-2', className)}>
      {shown.map((a) => {
        const Icon = CATEGORY_ICON[a.category];
        return (
          <li
            key={a.id}
            className={cn(
              'flex items-start gap-3 rounded-xl border p-3',
              a.earned ? 'border-border bg-card' : 'border-dashed border-border bg-card/40 opacity-70',
            )}
          >
            <span
              className={cn(
                'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2',
                a.earned ? TIER_RING[a.tier] : 'border-border bg-secondary text-muted-foreground',
              )}
            >
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium text-foreground">{a.title}</p>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">{CATEGORY_LABEL[a.category]}</span>
              </div>
              {!a.earned && (
                <div className="mt-1.5 flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-info" style={{ width: `${Math.round(a.progress * 100)}%` }} />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{a.caption}</span>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/** Headline numbers for a worker — used in the People drawer and the summary. */
export function StatsStrip({ stats, className }: { stats: WorkProfileStats; className?: string }) {
  const rate = scrapRate(stats.units_total, stats.scrap_total);
  const cells: Array<{ label: string; value: string }> = [
    { label: 'Today', value: String(stats.units_today) },
    { label: 'This week', value: String(stats.units_7d) },
    { label: 'Lifetime', value: stats.units_total.toLocaleString() },
    { label: 'Scrap rate', value: rate === null ? '—' : `${(rate * 100).toFixed(1)}%` },
    { label: 'Avg cycle (30d)', value: formatDuration(stats.avg_cycle_seconds_30d) },
    { label: 'Best cycle', value: formatDuration(stats.best_cycle_seconds) },
    { label: 'Defects caught', value: String(stats.defects_reported) },
    { label: 'Safety flags', value: String(stats.safety_total) },
    { label: 'Ideas', value: `${stats.suggestions_total}${stats.suggestions_adopted ? ` (${stats.suggestions_adopted} adopted)` : ''}` },
    { label: 'Check-in streak', value: `${stats.checkin_streak}d` },
  ];
  return (
    <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-5', className)}>
      {cells.map((c) => (
        <div key={c.label}>
          <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{c.label}</dt>
          <dd className="text-lg font-medium text-foreground">{c.value}</dd>
        </div>
      ))}
    </dl>
  );
}
